import { useMemo } from "react";
import { useQuery } from "urql";
import { getFragmentData, graphql } from "../client/gql";
import type { SearchData } from "../utils/globalSearch";

// Colocated fragments + queries: the global search only needs the handful of
// fields it maps into a SearchData snapshot. Names are globally unique
// (SearchData_*) so they never collide with the pim* list-page operations.
const SearchData_Organization = graphql(`
  fragment SearchData_Organization on Organization {
    uuid
    name
    city
  }
`);

const SearchData_OrganizationsQuery = graphql(`
  query SearchData_Organizations {
    organizations {
      ...SearchData_Organization
    }
  }
`);

const SearchData_User = graphql(`
  fragment SearchData_User on User {
    uuid
    email
    profiles {
      uuid
      listName
    }
  }
`);

const SearchData_UsersQuery = graphql(`
  query SearchData_Users {
    users {
      ...SearchData_User
    }
  }
`);

const SearchData_Application = graphql(`
  fragment SearchData_Application on Application {
    uuid
    name
    clientId
  }
`);

const SearchData_ApplicationsQuery = graphql(`
  query SearchData_Applications {
    applications {
      ...SearchData_Application
    }
  }
`);

const SearchData_AuthClient = graphql(`
  fragment SearchData_AuthClient on AuthClient {
    clientId
    clientName
  }
`);

const SearchData_AuthClientsQuery = graphql(`
  query SearchData_AuthClients {
    authClients {
      ...SearchData_AuthClient
    }
  }
`);

/**
 * Builds the ⌘K global-search snapshot from the list queries. Runs once at the
 * shell level; urql caches the results so per-page navigation reuses them.
 */
export function useSearchData(): SearchData {
  const [{ data: orgData }] = useQuery({ query: SearchData_OrganizationsQuery });
  const [{ data: userData }] = useQuery({ query: SearchData_UsersQuery });
  const [{ data: appData }] = useQuery({ query: SearchData_ApplicationsQuery });
  const [{ data: clientData }] = useQuery({ query: SearchData_AuthClientsQuery });

  const orgs = getFragmentData(SearchData_Organization, orgData?.organizations ?? []);
  const users = getFragmentData(SearchData_User, userData?.users ?? []);
  const apps = getFragmentData(SearchData_Application, appData?.applications ?? []);
  const clients = getFragmentData(SearchData_AuthClient, clientData?.authClients ?? []);

  return useMemo(
    () => ({
      orgs: orgs.map((o) => ({
        id: o.uuid,
        name: o.name,
        city: o.city ?? undefined,
      })),
      persons: users.map((u) => ({
        id: u.uuid,
        name: u.profiles?.[0]?.listName || u.email || u.uuid,
        email: u.email ?? undefined,
      })),
      apps: apps.map((a) => ({
        uuid: a.uuid,
        name: a.name,
        clientId: a.clientId,
      })),
      clients: clients.map((c) => ({
        clientId: c.clientId,
        clientName: c.clientName ?? c.clientId,
      })),
    }),
    [orgs, users, apps, clients],
  );
}
